// Written by Claude Code on 2026-05-04
// User prompt: Track 1 YC Sprint - demo onboarding flow for first-time users

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, CheckCircleIcon, ZapIcon, ShieldCheckIcon, FileTextIcon } from 'lucide-react';
import { Portal } from './Portal';
import { useModalA11y } from '../hooks/useModalA11y';

// Two decks. An employee opening the app needs to know how to request leave; only an
// admin audience has any use for what the platform covers as a product. Before this split
// everyone saw the admin deck, including its roadmap slide.
const ADMIN_SLIDES = [
  {
    icon: ZapIcon,
    iconColor: 'text-teal-500',
    iconBg: 'bg-teal-50',
    title: 'Welcome to Certify',
    subtitle: 'AI-native leave management',
    body: 'Certify runs the full leave lifecycle — FMLA, ADA, and state programs. Certifications are reviewed the moment they arrive, every deadline is tracked in calendar days per federal rules, and the audit trail builds itself. It connects to the HRIS you already use rather than replacing it.',
  },
  {
    icon: FileTextIcon,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-50',
    title: 'Upload → AI analyzes → HR acts',
    subtitle: 'Your 30-second certification workflow',
    body: null,
    steps: [
      { label: 'Employee or provider uploads the WH-380 certification', tag: 'Automated' },
      { label: 'AI extracts fields and checks required signatures and dates', tag: '~15s' },
      { label: 'Compliance engine validates against FMLA and 18 state programs', tag: '~5s' },
      { label: 'HR gets a decision: approve, request cure, or flag missing info', tag: 'Instant' },
      { label: 'Audit packet generated — a defensible record of the case', tag: 'Auto' },
    ],
  },
  {
    icon: ShieldCheckIcon,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-50',
    title: 'Beyond the certification',
    subtitle: 'What Certify covers',
    body: null,
    roadmap: [
      { phase: 'Live', label: 'FMLA, ADA/PWFA accommodations, and state paid leave',        active: true  },
      { phase: 'Live', label: 'Week-by-week pay estimates and benefit recovery during leave', active: true  },
      { phase: 'Live', label: 'Two-way sync with 25 HRIS and payroll systems',               active: true  },
      { phase: 'Next', label: 'Voice for Clara, and e-filing beyond Colorado FAMLI',          active: false },
    ],
  },
];

const EMPLOYEE_SLIDES = [
  {
    icon: ZapIcon,
    iconColor: 'text-teal-500',
    iconBg: 'bg-teal-50',
    title: 'Welcome to Certify',
    subtitle: 'Leave, without the paperwork maze',
    body: 'Certify walks you through requesting leave in plain language — no FMLA jargon. You can see where your request stands at any time, and what, if anything, is still needed from you.',
  },
  {
    icon: FileTextIcon,
    iconColor: 'text-indigo-500',
    iconBg: 'bg-indigo-50',
    title: 'Requesting leave',
    subtitle: 'What the process looks like',
    body: null,
    steps: [
      { label: 'Start a request — a guided form asks for your dates and leave type', tag: 'Guided' },
      { label: 'If a medical certification is needed, we contact your provider', tag: 'Automated' },
      { label: 'Track status and anything outstanding under My Leave', tag: 'Live' },
      { label: 'Plan ahead with a week-by-week pay estimate before you commit', tag: 'Optional' },
    ],
  },
  {
    icon: ShieldCheckIcon,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-50',
    title: 'Ask Clara anything',
    subtitle: 'Answers without calling HR',
    body: 'Clara can tell you whether you are eligible, how much leave you have left, and what happens to your pay and benefits while you are out. Anything you model in Plan Leave stays private to you until you submit a request.',
  },
];

export default function DemoWelcome({ userId, role }) {
  const [visible, setVisible] = useState(false);
  const [slide, setSlide] = useState(0);

  const slides = role === 'employee' ? EMPLOYEE_SLIDES : ADMIN_SLIDES;

  // Versioned key: the v1 deck described Certify as an HRIS that would replace BambooHR and
  // Paylocity, which are now integrations. Anyone who dismissed that copy should see the
  // corrected tour rather than keep a stale dismissal.
  const storageKey = userId ? `certify_welcome_v2_${userId}` : null;

  useEffect(() => {
    if (!storageKey) return;
    if (!localStorage.getItem(storageKey)) {
      setVisible(true);
    }
  }, [storageKey]);

  function dismiss() {
    if (storageKey) localStorage.setItem(storageKey, '1');
    setVisible(false);
  }

  function next() {
    if (slide < slides.length - 1) {
      setSlide(s => s + 1);
    } else {
      dismiss();
    }
  }

  const current = slides[Math.min(slide, slides.length - 1)];
  const Icon = current.icon;
  const isLast = slide === slides.length - 1;

  useModalA11y(dismiss, visible);

  return (
    <Portal>
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-0">
              <div className="flex gap-1.5">
                {slides.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1 rounded-full transition-all ${i === slide ? 'w-6 bg-teal-500' : 'w-2 bg-slate-200'}`}
                  />
                ))}
              </div>
              <button
                onClick={dismiss}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-4">
              <div className={`w-12 h-12 rounded-2xl ${current.iconBg} flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${current.iconColor}`} />
              </div>

              <div>
                <h2 className="text-xl font-semibold text-slate-900">{current.title}</h2>
                <p className="text-sm text-teal-600 font-medium mt-0.5">{current.subtitle}</p>
              </div>

              {current.body && (
                <p className="text-sm text-slate-600 leading-relaxed">{current.body}</p>
              )}

              {current.steps && (
                <div className="space-y-2">
                  {current.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-teal-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </div>
                      <p className="text-sm text-slate-700 flex-1">{step.label}</p>
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded shrink-0">
                        {step.tag}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {current.roadmap && (
                <div className="space-y-2">
                  {current.roadmap.map((item, i) => (
                    <div key={i} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl ${item.active ? 'bg-teal-50 border border-teal-100' : 'bg-slate-50'}`}>
                      <CheckCircleIcon className={`w-4 h-4 shrink-0 mt-0.5 ${item.active ? 'text-teal-500' : 'text-slate-300'}`} />
                      <div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${item.active ? 'text-teal-600' : 'text-slate-400'}`}>
                          {item.phase}
                        </span>
                        <p className={`text-sm ${item.active ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>{item.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 flex items-center justify-between">
              <button onClick={dismiss} className="text-sm text-slate-400 hover:text-slate-600 transition-colors">
                Skip tour
              </button>
              <button
                onClick={next}
                className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 rounded-xl transition-colors"
              >
                {isLast ? 'Get started' : 'Next'}
                <ArrowRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </Portal>
  );
}
